/**
 * Message Item - Individual message with actions, reactions, etc.
 */

import React, { useState } from 'react';
import { Dropdown, Button, Avatar, Typography, Space, Tooltip } from 'antd';
import { 
  MoreOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CopyOutlined,
  SmileOutlined,
  UserOutlined,
  PushpinOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PresenceIndicator } from './PresenceIndicator';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './MessageItem.css';

dayjs.extend(relativeTime);

const { Text } = Typography;

// Helper function to construct full URL for attachments
const getAttachmentUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Construct full URL using the same logic as API config
  const hostname = window.location.hostname.toLowerCase();
  const isProduction = hostname === 'patelhrms.com' 
    || hostname.includes('patelhrms.com')
    || hostname === 'vitreous.support'
    || hostname.includes('vitreous.support')
    || hostname === 'www.vitreous.support';
  const API_ORIGIN = isProduction ? window.location.origin : 'http://localhost:5000';
  
  // Ensure url starts with /
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${path}`;
};

interface MessageItemProps {
  message: {
    id: string;
    channelId: string;
    userId: string;
    text: string;
    contentType: string;
    attachmentUrl?: string;
    attachmentName?: string;
    threadCount: number;
    createdAt: string;
    updatedAt?: string;
    isPinned?: boolean;
    isEdited?: boolean;
    reactions?: Array<{
      emoji: string;
      count: number;
      userIds: string[];
      hasUserReaction?: boolean;
    }>;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  showAvatar: boolean;
  userPresence?: {
    status: 'online' | 'away' | 'busy' | 'offline';
  };
  onReply?: () => void;
  onEdit?: (messageId: string, currentText: string) => void;
  onDelete?: () => void;
  onPin?: () => void;
  onReaction?: (messageId: string, emoji: string) => void;
  onOpenThread?: (messageId: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  showAvatar,
  userPresence,
  onReply,
  onEdit,
  onDelete,
  onPin,
  onReaction,
  onOpenThread,
}) => {
  const { user } = useAuth();
  const [hovered, setHovered] = useState(false);
  const isOwnMessage = user?.id === message.userId;

  const formatTime = (timestamp: string) => {
    return dayjs(timestamp).format('h:mm A');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message.text);
  };

  const menuItems = [
    {
      key: 'pin',
      icon: <PushpinOutlined />,
      label: message.isPinned ? 'Unpin' : 'Pin',
      onClick: onPin,
    },
    {
      key: 'reply',
      icon: <UserOutlined />,
      label: message.threadCount > 0 ? 'Reply in thread' : 'Reply',
      onClick: () => {
        if (message.threadCount > 0 && onOpenThread) {
          onOpenThread(message.id);
        } else if (onReply) {
          onReply();
        }
      },
    },
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: 'Copy',
      onClick: handleCopy,
    },
    ...(isOwnMessage ? [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        onClick: () => onEdit && onEdit(message.id, message.text),
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true,
        onClick: onDelete,
      },
    ] : []),
  ];

  return (
    <div 
      className={`message-item-wrapper ${isOwnMessage ? 'own-message' : ''} ${hovered ? 'hovered' : ''} ${message.isPinned ? 'pinned' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="message-item-content">
        {showAvatar ? (
          <div className="message-avatar-container">
            <PresenceIndicator
              status={userPresence?.status || 'offline'}
              size="small"
            >
              <Avatar
                size={36}
                icon={<UserOutlined />}
                style={{ 
                  backgroundColor: '#350d36',
                  color: '#ffffff',
                  fontSize: 15,
                  fontWeight: 700
                }}
              />
            </PresenceIndicator>
          </div>
        ) : (
          <div className="message-avatar-container" style={{ width: 36, marginRight: 8 }} />
        )}
        <div className="message-body">
          {showAvatar && (
            <div className="message-header">
              <span className="message-author">
                {message.user.firstName} {message.user.lastName}
              </span>
              {message.isPinned && (
                <Tooltip title="Pinned">
                  <PushpinOutlined className="message-pin-indicator" />
                </Tooltip>
              )}
              <span className="message-timestamp">
                {formatTime(message.createdAt)}
              </span>
            </div>
          )}
          {!showAvatar && message.isPinned && (
            <div className="message-pin-indicator-inline">
              <Tooltip title="Pinned">
                <PushpinOutlined className="message-pin-indicator" />
              </Tooltip>
            </div>
          )}
          <div className="message-text-wrapper">
            <div className="message-text">
              {message.text}
            </div>
            {message.attachmentUrl && (
              <div className="message-attachment">
                <a href={getAttachmentUrl(message.attachmentUrl) || '#'} target="_blank" rel="noopener noreferrer">
                  📎 {message.attachmentName || 'Attachment'}
                </a>
              </div>
            )}
            {message.threadCount > 0 && (
              <div 
                className="message-thread-link" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenThread) {
                    onOpenThread(message.id);
                  } else if (onReply) {
                    onReply();
                  }
                }}
              >
                {message.threadCount} {message.threadCount === 1 ? 'reply' : 'replies'}
              </div>
            )}
            {message.isEdited && (
              <span className="message-edited-indicator" title="Edited">
                (edited)
              </span>
            )}
            {message.reactions && message.reactions.length > 0 && (
              <div className="message-reactions">
                {message.reactions.map((reaction, idx) => (
                  <button
                    key={idx}
                    className={`message-reaction ${reaction.hasUserReaction ? 'user-reacted' : ''}`}
                    onClick={() => onReaction && onReaction(message.id, reaction.emoji)}
                    title={`${reaction.count} ${reaction.count === 1 ? 'reaction' : 'reactions'}`}
                  >
                    <span className="reaction-emoji">{reaction.emoji}</span>
                    <span className="reaction-count">{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {hovered && (
          <div className="message-actions">
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                type="text" 
                icon={<MoreOutlined />} 
                size="small"
                className="message-action-button"
              />
            </Dropdown>
            <Dropdown
              menu={{
                items: [
                  { key: '👍', label: '👍', onClick: () => onReaction && onReaction(message.id, '👍') },
                  { key: '❤️', label: '❤️', onClick: () => onReaction && onReaction(message.id, '❤️') },
                  { key: '😂', label: '😂', onClick: () => onReaction && onReaction(message.id, '😂') },
                  { key: '🎉', label: '🎉', onClick: () => onReaction && onReaction(message.id, '🎉') },
                  { key: '🔥', label: '🔥', onClick: () => onReaction && onReaction(message.id, '🔥') },
                  { key: '👏', label: '👏', onClick: () => onReaction && onReaction(message.id, '👏') },
                ]
              }}
              trigger={['click']}
              placement="topRight"
            >
              <Button 
                type="text" 
                icon={<SmileOutlined />} 
                size="small"
                className="message-action-button"
                title="Add reaction"
              />
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
};

